import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import createVuetify from './plugins/vuetify'
import './plugins/pp-vue-library'
import Vuelidate from 'vuelidate'
import breakpoints from '@/styles/breakpoints.scss'
import PpVueAnalytics from 'pp-vue-analytics'
import { datadogRum } from '@datadog/browser-rum'
import { getSessionItem, setSessionItem } from '@/utils/storage'
import { primaryButtonTheme, sanitizeHex, secondaryLabelColor } from '@/utils/color'
import { configureCampaignAnalytics } from '@/analytics'
import { stripEmailFromLocation } from '@/utils/navigation'

Vue.config.productionTip = false

Vue.use(Vuelidate)

const { params, email: emailParam } = stripEmailFromLocation(window.location, window.history)

// Persist identity params to sessionStorage so they survive refreshes.
// Selection state (location, provider, etc.) intentionally resets on refresh.
const SESSION_PARAM_KEYS = ['practiceUuid', 'practiceName', 'gaId']

SESSION_PARAM_KEYS.forEach(key => {
  const value = params.get(key)
  if (value !== null) setSessionItem(`reputation_${key}`, value)
})

function getParam (key) {
  return params.get(key) || getSessionItem(`reputation_${key}`)
}

function getUrlParam (key) {
  return params.get(key)
}

const appGaId = process.env.VUE_APP_GA_ID
const practiceGaId = getParam('gaId')
Vue.use(PpVueAnalytics, router, {
  appGaId,
  practiceGaId,
  category: 'reputation'
})

configureCampaignAnalytics(Vue.prototype.$gtag, {
  utmMedium: getUrlParam('utm_medium'),
  utmTerm: getUrlParam('utm_term'),
  utmContent: getUrlParam('utm_content'),
  practiceName: getParam('practiceName'),
  hostname: window.location.hostname
})

Vue.prototype.$ppUi = {
  isMobile: window.innerWidth < breakpoints.xs
}

const root = document.documentElement
const primaryVal = sanitizeHex(params.get('primary'), '#555f7e')
const buttonTheme = primaryButtonTheme(primaryVal)
root.style.setProperty('--color-primary', primaryVal)
root.style.setProperty('--color-primary-dark', buttonTheme.hoverColor)
root.style.setProperty('--pp-primary-button-text-custom-color', buttonTheme.textColor)
root.style.setProperty('--pp-secondary-button-text-color', secondaryLabelColor(primaryVal))
root.style.setProperty('--pp-primary-button-hover-bg', buttonTheme.hoverColor)

const vuetify = createVuetify(primaryVal)

const datadogAppId = process.env.VUE_APP_DATADOG_APPLICATION_ID
const datadogClientToken = process.env.VUE_APP_DATADOG_CLIENT_TOKEN
const datadogEnabled = process.env.VUE_APP_STAGE &&
  process.env.VUE_APP_STAGE !== 'local' &&
  datadogAppId && datadogClientToken

if (datadogEnabled) {
  // Monitoring must never break the widget, so a bad config fails quietly.
  try {
    datadogRum.init({
      applicationId: datadogAppId,
      clientToken: datadogClientToken,
      env: process.env.VUE_APP_STAGE,
      version: process.env.VUE_APP_VERSION,
      sampleRate: 100,
      site: 'us5.datadoghq.com',
      service: 'reputation-widget',
      // The widget runs in a third-party iframe, so the session cookie must be
      // SameSite=None; Secure to survive.
      useCrossSiteSessionCookie: true,
      trackInteractions: true,
      silentMultipleInit: true
    })
    datadogRum.addRumGlobalContext('practiceUuid', getParam('practiceUuid'))
  } catch (error) {
    console.error('Datadog RUM failed to initialize', error)
  }
}

store.dispatch('reputation/initFromParams', {
  source: getUrlParam('source'),
  practiceUuid: getParam('practiceUuid'),
  practiceName: getParam('practiceName'),
  locationId: getUrlParam('location_id'),
  providerId: getUrlParam('provider_id'),
  ppLocation: getUrlParam('pp-location'),
  providerHash: getUrlParam('provider'),
  email: emailParam,
  recommend: getUrlParam('recommend'),
  review: getUrlParam('review'),
  campaignId: getUrlParam('campaign_id'),
  recipientId: getUrlParam('recipient_id')
})

// Match the original widget preload: one routing call from URL context, not
// from later location/provider clicks or the Leave review button.
store.dispatch('reputation/bootstrapReviewRouting').catch(error => {
  console.error('Failed to bootstrap review routing', error)
})

new Vue({
  router,
  store,
  vuetify,
  render: h => h(App)
}).$mount('#app')
